import React, { useEffect, useRef, useState, useCallback } from 'react';

interface FloatingParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  phase: number;
}

interface DecodedEnvelope {
  sampleRate: number;
  duration: number;
  channelData: Float32Array;
}

const STORAGE_KEY_PAUSED = 'nav_audio_is_paused';
const STORAGE_KEY_MUTED = 'nav_audio_is_muted';

const NavbarAudioMonitor: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const decodedRef = useRef<DecodedEnvelope | null>(null);

  // Initial setup is ON HOLD (paused = true) by default, and remembers state across page changes
  const [isPaused, setIsPaused] = useState<boolean>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY_PAUSED);
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  const [isMuted, setIsMuted] = useState<boolean>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY_MUTED);
      return saved !== null ? saved === 'true' : false;
    } catch {
      return false;
    }
  });

  // Refs for 60fps render loop synchronization
  const pausedRef = useRef<boolean>(isPaused);
  const mutedRef = useRef<boolean>(isMuted);

  useEffect(() => {
    pausedRef.current = isPaused;
    try {
      sessionStorage.setItem(STORAGE_KEY_PAUSED, String(isPaused));
    } catch {
      // Ignore storage errors
    }
  }, [isPaused]);

  useEffect(() => {
    mutedRef.current = isMuted;
    try {
      sessionStorage.setItem(STORAGE_KEY_MUTED, String(isMuted));
    } catch {
      // Ignore storage errors
    }
  }, [isMuted]);

  // Decode /song.mp3 into an in-memory PCM buffer using OfflineAudioContext.
  // Never calls createMediaElementSource(audio) so speaker output is never hijacked.
  useEffect(() => {
    let cancelled = false;

    const decodeSongForVisualizer = async () => {
      try {
        const res = await fetch('/song.mp3');
        if (!res.ok || cancelled) return;
        const arrayBuf = await res.arrayBuffer();
        if (cancelled) return;

        const OfflineCtx =
          window.OfflineAudioContext ||
          (window as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext })
            .webkitOfflineAudioContext;
        if (!OfflineCtx) return;

        const offlineCtx = new OfflineCtx(1, 44100, 44100);
        const audioBuffer = await offlineCtx.decodeAudioData(arrayBuf);
        if (cancelled) return;

        const ch0 = audioBuffer.getChannelData(0);
        decodedRef.current = {
          sampleRate: audioBuffer.sampleRate,
          duration: audioBuffer.duration,
          channelData: new Float32Array(ch0),
        };
      } catch {
        // Fallback medical heartbeat simulator in render loop handles visualization
      }
    };

    decodeSongForVisualizer();

    return () => {
      cancelled = true;
    };
  }, []);

  // Configure <audio> element without auto-starting unless state was already set to playing
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.loop = true;
    audio.volume = 0.85;
    audio.muted = mutedRef.current;

    if (!pausedRef.current) {
      audio.play().catch(() => {
        setIsPaused(true);
        pausedRef.current = true;
      });
    } else {
      audio.pause();
    }
  }, []);

  // Clicking the ECG / rhythm bars animation toggles Mute / Unmute when playing
  const handleToggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Only toggle mute when playing; when on hold, only the Play button starts playback
    if (isPaused) {
      return;
    }

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    mutedRef.current = nextMuted;
    audio.muted = nextMuted;
  }, [isMuted, isPaused]);

  // Dedicated Play / Hold toggle button — starts playing only when clicked
  const handleTogglePlayPause = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const audio = audioRef.current;
      if (!audio) return;

      if (isPaused || audio.paused) {
        setIsPaused(false);
        pausedRef.current = false;
        if (isMuted) {
          setIsMuted(false);
          mutedRef.current = false;
          audio.muted = false;
        } else {
          audio.muted = false;
        }
        audio.play().catch(() => {
          setIsPaused(true);
          pausedRef.current = true;
        });
      } else {
        setIsPaused(true);
        pausedRef.current = true;
        audio.pause();
      }
    },
    [isPaused, isMuted]
  );

  // 60fps Hospital ECG Heartbeat Gadget + Floating/Growing/Scaling Rhythm Bars
  // Runs the full medical device heartbeat simulation ALL THE TIME (both on hold and while playing)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      const targetEl = canvas.parentElement || canvas;
      const rect = targetEl.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(36, Math.floor(rect.width));
      height = Math.max(22, Math.floor(rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement || canvas);

    // State for growing/scaling bars & floating peak caps
    const maxBars = 56;
    const smoothBars = new Float32Array(maxBars);
    const peakCaps = new Float32Array(maxBars);
    const peakVel = new Float32Array(maxBars);
    const bandEnergies = new Float32Array(maxBars);

    // Floating bioluminescent rhythm particles
    const numParticles = 18;
    const particles: FloatingParticle[] = Array.from({ length: numParticles }, (_, i) => ({
      x: (i / numParticles) * 300,
      y: 14,
      vx: 8 + (i % 5) * 4,
      vy: (i % 2 === 0 ? -1 : 1) * (3 + (i % 3) * 2),
      size: 1.3 + (i % 3) * 0.6,
      alpha: 0.35 + (i % 4) * 0.15,
      phase: i * 0.7,
    }));

    // Doctor's hospital ECG P-Q-R-S-T waveform evaluator
    const evalPQRST = (cyclePos: number, intensity: number): number => {
      const p = ((cyclePos % 1.0) + 1.0) % 1.0;
      const pWave = 0.24 * Math.exp(-Math.pow((p - 0.18) / 0.042, 2));
      const qDip = -0.28 * Math.exp(-Math.pow((p - 0.33) / 0.016, 2));
      const rSpike = (0.98 + 0.32 * intensity) * Math.exp(-Math.pow((p - 0.37) / 0.016, 2));
      const sDip = -0.44 * Math.exp(-Math.pow((p - 0.41) / 0.018, 2));
      const tWave = 0.34 * Math.exp(-Math.pow((p - 0.62) / 0.055, 2));
      return pWave + qDip + rSpike + sDip + tWave;
    };

    let prevBassEnergy = 0;
    let beatPulseBoost = 0;
    let scrollPhase = 0;
    let lastTime = performance.now();

    const render = (now: number) => {
      const dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;
      const t = now * 0.001;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      const isAudioPlaying = !pausedRef.current && !mutedRef.current;
      const audioEl = audioRef.current;
      const decoded = decodedRef.current;

      // Extract real-time energy & frequency bands from the decoded PCM samples of song.mp3 when playing
      let bassEnergy = 0;
      let midEnergy = 0;
      let hasLivePCM = false;

      if (
        isAudioPlaying &&
        decoded &&
        audioEl &&
        !audioEl.paused &&
        decoded.duration > 0
      ) {
        const curTime = audioEl.currentTime % decoded.duration;
        const centerSample = Math.floor(curTime * decoded.sampleRate);
        const pcm = decoded.channelData;
        const winSize = 1024;
        const startIdx = Math.max(0, Math.min(pcm.length - winSize, centerSample - (winSize >> 1)));

        let sumSq = 0;
        let lowPass = 0;
        let sumLowSq = 0;
        let sumDiffSq = 0;

        for (let i = 0; i < winSize; i += 2) {
          const s = pcm[startIdx + i];
          const prev = i > 0 ? pcm[startIdx + i - 2] : s;
          sumSq += s * s;
          lowPass = lowPass * 0.88 + s * 0.12;
          sumLowSq += lowPass * lowPass;
          const diff = s - prev;
          sumDiffSq += diff * diff;
        }

        const count = winSize >> 1;
        const rms = Math.sqrt(sumSq / count);
        const bassRms = Math.sqrt(sumLowSq / count);
        const highRms = Math.sqrt(sumDiffSq / count);

        bassEnergy = Math.min(1.0, bassRms * 5.2);
        midEnergy = Math.min(1.0, rms * 3.8 + highRms * 2.2);
        hasLivePCM = rms > 0.002;

        const samplesPerBar = Math.max(8, Math.floor(winSize / maxBars));
        for (let b = 0; b < maxBars; b++) {
          let segSum = 0;
          const segStart = startIdx + b * samplesPerBar;
          for (let j = 0; j < samplesPerBar; j++) {
            const v = pcm[Math.min(pcm.length - 1, segStart + j)];
            segSum += Math.abs(v);
          }
          bandEnergies[b] = Math.min(1.0, (segSum / samplesPerBar) * 4.2);
        }
      }

      // Continuous medical heartbeat simulation energy (always active, enhanced by live PCM when playing)
      const synthBeat = Math.pow(Math.max(0, Math.sin(t * Math.PI * 2.1)), 6.0);
      const effectiveBass = hasLivePCM
        ? bassEnergy
        : 0.38 + 0.48 * synthBeat + 0.12 * Math.sin(t * 4.7);
      const effectiveMid = hasLivePCM
        ? midEnergy
        : 0.32 + 0.35 * Math.sin(t * 3.3 + 1.1) * Math.cos(t * 5.4);

      // Detect beat transients for dynamic scaling bursts
      const bassDelta = effectiveBass - prevBassEnergy;
      prevBassEnergy = effectiveBass * 0.75 + prevBassEnergy * 0.25;
      if (bassDelta > 0.065 && beatPulseBoost < 0.45) {
        beatPulseBoost = 1.0;
      }
      beatPulseBoost *= Math.pow(0.06, dt);

      const overallEnergy = Math.min(
        1.0,
        effectiveBass * 0.65 + effectiveMid * 0.45 + beatPulseBoost * 0.35
      );

      const centerY = height * 0.52;

      // 1. Subtle Hospital Monitor Background Grid & Baseline
      ctx.strokeStyle = 'rgba(57, 255, 20, 0.10)';
      ctx.lineWidth = 1;
      const gridStep = 14;
      ctx.beginPath();
      for (let x = 0; x < width; x += gridStep) {
        ctx.moveTo(x + 0.5, 1);
        ctx.lineTo(x + 0.5, height - 1);
      }
      ctx.stroke();

      ctx.save();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = 'rgba(57, 255, 20, 0.28)';
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();
      ctx.restore();

      // 2. Growing, Scaling, Floating Rhythm Bars + Floating Peak Caps (always active)
      const barSpacing = 5.5;
      const numBars = Math.min(maxBars, Math.max(16, Math.floor((width - 6) / barSpacing)));
      const baseBarW = 2.8;

      for (let i = 0; i < numBars; i++) {
        const normIdx = i / Math.max(1, numBars - 1);
        const env = Math.sin(normIdx * Math.PI);
        let targetVal: number;

        if (hasLivePCM) {
          const rawBand = bandEnergies[i % maxBars];
          const waveMod =
            0.12 * Math.sin(t * 5.8 - i * 0.42) +
            0.08 * Math.cos(t * 3.4 + i * 0.31);
          targetVal = Math.max(
            0.10,
            Math.min(
              1.0,
              (rawBand * 0.85 + effectiveBass * 0.25 + waveMod + beatPulseBoost * 0.22) *
                (0.6 + 0.4 * env)
            )
          );
        } else {
          const wave1 = Math.sin(t * 5.2 - i * 0.38) * 0.5 + 0.5;
          const wave2 = Math.cos(t * 8.1 + i * 0.62) * 0.5 + 0.5;
          targetVal =
            0.14 +
            (0.42 * Math.pow(wave1, 1.8) + 0.26 * wave2 + 0.24 * synthBeat) *
              (0.55 + 0.45 * env);
        }

        smoothBars[i] += (targetVal - smoothBars[i]) * Math.min(1.0, dt * 18.0);
        const val = smoothBars[i];

        if (val > peakCaps[i]) {
          peakCaps[i] = Math.min(1.05, val + 0.06);
          peakVel[i] = 0;
        } else {
          peakVel[i] += dt * 0.9;
          peakCaps[i] = Math.max(val, peakCaps[i] - peakVel[i] * dt);
        }

        const x = 3 + i * ((width - 6) / numBars);
        const maxBarH = height * 0.84;
        const barH = Math.max(2.5, val * maxBarH);

        const floatY = Math.sin(t * 4.4 + i * 0.48) * 1.6;
        const barTop = centerY - barH * 0.5 + floatY;

        const scaleW = baseBarW * (0.82 + 0.45 * val);
        const barAlpha = 0.26 + 0.56 * val;

        ctx.fillStyle = `rgba(57, 255, 20, ${barAlpha.toFixed(3)})`;
        ctx.fillRect(x - scaleW * 0.5, barTop, scaleW, barH);

        const capOffset = peakCaps[i] * maxBarH * 0.5;
        const capTopY = Math.max(1, centerY - capOffset + floatY - 2.2);
        const capBotY = Math.min(height - 2.5, centerY + capOffset + floatY + 0.6);

        ctx.fillStyle = val > 0.52 ? '#ccffcc' : '#39ff14';
        ctx.fillRect(x - scaleW * 0.5, capTopY, scaleW, 1.8);
        ctx.fillStyle = 'rgba(57, 255, 20, 0.55)';
        ctx.fillRect(x - scaleW * 0.5, capBotY, scaleW, 1.4);
      }

      // 3. Floating Bioluminescent Rhythm Nodes drifting across the monitor
      for (let pIdx = 0; pIdx < particles.length; pIdx++) {
        const pt = particles[pIdx];
        pt.x += pt.vx * (1.0 + overallEnergy * 1.4) * dt;
        pt.y += Math.sin(t * 3.5 + pt.phase) * 9.0 * dt;

        if (pt.x > width) pt.x = 0;
        if (pt.y < 3) pt.y = height - 3;
        if (pt.y > height - 3) pt.y = 3;

        const pScale = pt.size * (0.85 + 0.55 * overallEnergy);
        ctx.fillStyle = `rgba(160, 255, 160, ${(pt.alpha * (0.6 + 0.4 * overallEnergy)).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pScale, 0, Math.PI * 2);
        ctx.fill();
      }

      // 4. Doctor's Hospital Heartbeat Monitor (Scrolling P-Q-R-S-T ECG Waveform + Sweep Cursor)
      const scrollSpeed = 0.85 + overallEnergy * 0.65;
      scrollPhase = (scrollPhase + dt * scrollSpeed) % 100.0;

      const cyclesAcrossWidth = Math.max(2.2, width / 88);
      const sweepX = (t * 86) % width;

      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.shadowColor = '#39ff14';
      ctx.shadowBlur = 9;
      ctx.lineWidth = 2.1;
      ctx.strokeStyle = '#39ff14';

      const stepPx = 2;
      ctx.beginPath();
      let leadY = centerY;

      for (let x = 2; x <= width - 4; x += stepPx) {
        const normX = x / Math.max(1, width - 6);
        const cyclePos = normX * cyclesAcrossWidth - scrollPhase;

        const pqrst = evalPQRST(cyclePos, overallEnergy);
        const bIdx = Math.floor(normX * (maxBars - 1));
        const liveRipple = hasLivePCM
          ? (bandEnergies[bIdx] - 0.25) * 0.24 * Math.sin(normX * 32.0 - t * 12.0)
          : Math.sin(normX * 26.0 - t * 9.0) * 0.06 * overallEnergy;
        const yOffset = (pqrst * (0.82 + 0.28 * overallEnergy) + liveRipple) * (height * 0.44);

        const py = Math.max(2, Math.min(height - 2, centerY - yOffset));
        if (x === 2) {
          ctx.moveTo(x, py);
        } else {
          ctx.lineTo(x, py);
        }
        if (Math.abs(x - sweepX) <= stepPx) {
          leadY = py;
        }
      }
      ctx.stroke();

      ctx.shadowBlur = 0;
      ctx.lineWidth = 1.0;
      ctx.strokeStyle = '#d8ffd8';
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = '#39ff14';
      ctx.shadowBlur = 11;
      ctx.beginPath();
      ctx.arc(sweepX, leadY, 2.6 + beatPulseBoost * 1.3, 0, Math.PI * 2);
      ctx.fill();

      const grad = ctx.createLinearGradient(sweepX - 22, 0, sweepX + 3, 0);
      grad.addColorStop(0, 'rgba(57, 255, 20, 0)');
      grad.addColorStop(0.85, 'rgba(57, 255, 20, 0.16)');
      grad.addColorStop(1, 'rgba(180, 255, 180, 0.34)');
      ctx.fillStyle = grad;
      ctx.fillRect(sweepX - 22, 1, 25, height - 2);

      ctx.restore();
      ctx.restore();

      rafId = requestAnimationFrame(render);
    };

    rafId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const isAudioActive = !isPaused && !isMuted;

  return (
    <div
      className="nav-audio-monitor is-active"
      onClick={handleToggleMute}
      role="button"
      tabIndex={0}
      aria-label={
        isPaused
          ? 'Audio on hold — click play button to start music'
          : isMuted
          ? 'Unmute background music'
          : 'Mute background music'
      }
      title={
        isPaused
          ? 'Medical ECG monitor (Audio on hold — click ▶ to play)'
          : isMuted
          ? 'Click ECG rhythm monitor to Unmute music'
          : 'Click ECG rhythm monitor to Mute music'
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggleMute();
        }
      }}
    >
      {/* Pure native <audio loop> — starts on hold and plays only when the user clicks Play */}
      <audio
        ref={audioRef}
        src="/song.mp3"
        loop
        preload="auto"
        style={{ display: 'none' }}
      />

      {/* Live Hospital ECG & Spectrum Bars Canvas spanning from CV to the end of the navbar */}
      <div className="nav-audio-canvas-wrap">
        <canvas ref={canvasRef} className="nav-audio-canvas" />
      </div>

      {/* Compact Medical/Terminal Audio Control HUD */}
      <div className="nav-audio-controls" onClick={handleTogglePlayPause}>
        <button
          type="button"
          className="nav-audio-btn"
          onClick={handleTogglePlayPause}
          title={isPaused ? 'Play music' : 'Hold / Pause music'}
          aria-label={isPaused ? 'Play music' : 'Hold / Pause music'}
        >
          {isPaused ? '▶' : '❚❚'}
        </button>
        <span
          className="nav-audio-status"
          title={isPaused ? 'Click to Play music' : 'Click to Hold / Pause music'}
        >
          <span className={`nav-audio-led ${isAudioActive ? 'led-on' : 'led-off'}`} />
          {isPaused ? 'HOLD' : isMuted ? 'MUTE' : 'LIVE'}
        </span>
      </div>
    </div>
  );
};

export default NavbarAudioMonitor;
